import { Toggle as TogglePrimitive } from '@base-ui/react/toggle';
import { ToggleGroup as ToggleGroupPrimitive } from '@base-ui/react/toggle-group';
import { cn } from '@mdvip/shared/lib/utils';
import { cva, type VariantProps } from 'class-variance-authority';
import * as React from 'react';

const toggleVariants = cva(
  "inline-flex cursor-pointer items-center justify-center gap-2 rounded-md text-sm font-medium whitespace-nowrap outline-none transition-colors hover:bg-muted hover:text-muted-foreground focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background focus-visible:z-10 disabled:pointer-events-none disabled:opacity-50 data-pressed:bg-accent data-pressed:text-accent-foreground [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
  {
    variants: {
      variant: {
        default: 'bg-transparent',
        outline: 'border border-input bg-transparent shadow-xs hover:bg-accent hover:text-accent-foreground',
        // Rounded-rectangle group (not a pill) — the pressed item gets a raised `bg-card` tile
        // (pure white, not `bg-background`, which in this theme is only 1.5% lighter than
        // `bg-muted` and reads as barely-distinguishable gray) with a soft shadow + hairline ring
        // and a slight lift, inside the bordered, inset-shadowed track.
        segmented:
          'rounded-xl bg-transparent text-foreground/70 transition-[color,background-color,box-shadow] duration-fast ease-standard active:brightness-[0.97] hover:bg-card/40 hover:text-foreground data-pressed:bg-card! data-pressed:text-foreground! data-pressed:shadow-md data-pressed:ring-1 data-pressed:ring-border data-pressed:-translate-y-px',
      },
      size: {
        default: 'h-9 min-w-9 px-2',
        sm: 'h-8 min-w-8 px-2',
        lg: 'h-10 min-w-10 px-3',
      },
    },
    defaultVariants: { variant: 'default', size: 'default' },
  },
);

const ToggleGroupContext = React.createContext<VariantProps<typeof toggleVariants>>({
  variant: 'default',
  size: 'default',
});

export interface ToggleProps extends VariantProps<typeof toggleVariants> {
  /** Unique value identifying this toggle inside a `ToggleGroup`. */
  value?: string;
  /** Controlled pressed state (standalone use, outside a `ToggleGroup`). */
  pressed?: boolean;
  /** Uncontrolled pressed state. @default false */
  defaultPressed?: boolean;
  onPressedChange?: (pressed: boolean) => void;
  /** @default false */
  disabled?: boolean;
  className?: string;
  children?: React.ReactNode;
}

function Toggle({ className, variant, size, ...props }: ToggleProps) {
  return <TogglePrimitive data-slot="toggle" className={cn(toggleVariants({ variant, size }), className)} {...props} />;
}

export interface ToggleGroupProps extends VariantProps<typeof toggleVariants> {
  /**
   * Values of the currently pressed items. With `multiple` left at its default `false`, only one
   * item stays pressed at a time — pressing another unpresses the rest, giving single-select
   * behavior (the value is still an array, just of length 0–1).
   */
  value?: readonly string[];
  defaultValue?: readonly string[];
  onValueChange?: (value: string[]) => void;
  /** @default false */
  disabled?: boolean;
  /** @default 'horizontal' */
  orientation?: 'horizontal' | 'vertical';
  /** Allow more than one item to stay pressed at once — a toolbar of independent toggles. @default false */
  multiple?: boolean;
  className?: string;
  children?: React.ReactNode;
}

function ToggleGroup({ className, variant, size, children, ...props }: ToggleGroupProps) {
  return (
    <ToggleGroupPrimitive
      data-slot="toggle-group"
      data-variant={variant}
      className={cn(
        'group/toggle-group flex w-fit items-center rounded-md data-[variant=outline]:shadow-xs',
        'data-[variant=segmented]:gap-1.5 data-[variant=segmented]:rounded-2xl data-[variant=segmented]:border data-[variant=segmented]:border-border data-[variant=segmented]:bg-muted data-[variant=segmented]:p-1.5 data-[variant=segmented]:shadow-inner',
        className,
      )}
      {...props}
    >
      <ToggleGroupContext.Provider value={{ variant, size }}>{children}</ToggleGroupContext.Provider>
    </ToggleGroupPrimitive>
  );
}

function ToggleGroupItem({ className, children, variant, size, ...props }: ToggleProps) {
  const context = React.useContext(ToggleGroupContext);
  return (
    <TogglePrimitive
      data-slot="toggle-group-item"
      data-variant={context.variant ?? variant}
      className={cn(
        toggleVariants({ variant: context.variant ?? variant, size: context.size ?? size }),
        // Edge-joining (flat inner corners) is an `outline`-only concern — `segmented` items are
        // independent rounded-xl tiles floating in the track, and unconditionally forcing
        // `rounded-none` here would beat `toggleVariants`' `rounded-xl` under tailwind-merge
        // (last conflicting utility wins), leaving only the first/last item's outer corners round.
        'min-w-0 flex-1 shrink-0 shadow-none focus:z-10 data-[variant=outline]:rounded-none data-[variant=outline]:first:rounded-l-md data-[variant=outline]:last:rounded-r-md data-[variant=outline]:border-l-0 data-[variant=outline]:first:border-l',
        className,
      )}
      {...props}
    >
      {children}
    </TogglePrimitive>
  );
}

export { Toggle, ToggleGroup, ToggleGroupItem, toggleVariants };
